import { AppSection, NomObjectClass } from '../../constants';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ViewChild,
  ViewContainerRef,
  computed,
  inject,
} from '@angular/core';
import { Dashboard, DataObject, Feed } from '../../models/core.models';
import { Observable, take, tap } from 'rxjs';
import {
  selectCoreAppSection,
  selectCurrentObject,
  selectCurrentObjectEditMode,
  selectPeriod,
  selectUser,
  selectUserAccessLevelGlobalAdmin,
  selectUserAccessLevelName,
  selectUserCompany,
} from '../../reducers';

import { AppFeatures } from '../../app.features';
import { AppState } from 'src/app/reducers';
import { DropdownAlertsComponent } from '../dropdowns/dropdown-alerts/dropdown-alerts.component';
import { DropdownNotificationsComponent } from '../dropdowns/dropdown-notifications/dropdown-notifications.component';
import { DropdownUserComponent } from '../dropdowns/dropdown-user/dropdown-user.component';
import { ObjectCreatorService } from '../../services/object-creator.service';
import { PresentationService } from '../../services/presentation.service';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { UserFacadeService } from '@services/user-facade.service';
import { environment } from '@env/environment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserPreferencesService } from '../../services/user-preferences.service';

import {
  HeaderTools,
  HeaderToolsService,
} from '../../services/header-tools.service';
import { injectUserAlertsCountQuery } from 'src/app/api/queries/alert.queries';
import {
  NotificationsScope,
  injectNotificationsCountQuery,
} from 'src/app/api/queries/notification.queries';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class HeaderComponent {
  public appName: string = environment.appName;
  NomObjectClass = NomObjectClass;
  public isMenuCollapsed: boolean = true;
  activeCompany: any;
  accessLevel: any;
  currentObject$: Observable<DataObject>;
  currentUser$: Observable<any>;

  currentObjectEditMode$: Observable<boolean>;
  isGlobalAdmin$: Observable<boolean>;

  /**
   * The two badge counts. Owned here rather than by a root service because the
   * header is the only reader, and `gcTime: Infinity` keeps both entries alive while
   * Presentation Mode has it unmounted - so a socket notification arriving then still
   * increments the badge.
   */
  private readonly user = inject(Store<AppState>).selectSignal(selectUser);

  private readonly countsScope = computed<NotificationsScope | null>(() => {
    const user = this.user();

    return user?.id
      ? { userId: user.id, companyId: user.active_company_id }
      : null;
  });

  private readonly alertsCountQuery = injectUserAlertsCountQuery(
    this.countsScope,
  );
  private readonly notificationsCountQuery = injectNotificationsCountQuery(
    this.countsScope,
  );

  readonly countAlerts = computed(() => this.alertsCountQuery.data() ?? 0);
  readonly countNotifications = computed(
    () => this.notificationsCountQuery.data() ?? 0,
  );

  /** Screen-registered header controls (date range / data-view toggle). */
  readonly headerTools$: Observable<HeaderTools | null> =
    inject(HeaderToolsService).currentTools$;

  private destroyRef = inject(DestroyRef);
  private readonly userPreferences = inject(UserPreferencesService);

  @ViewChild('alertsDropdownContainer', { read: ViewContainerRef })
  alertsContainer: ViewContainerRef;

  @ViewChild('notificationsDropdownContainer', { read: ViewContainerRef })
  notificationsContainer: ViewContainerRef;

  @ViewChild('userDropdownContainer', { read: ViewContainerRef })
  userContainer: ViewContainerRef;

  appSection: AppSection = 'analytics';
  activeMenu: 'alerts' | 'notifications' | 'user' | 'none' = 'none';

  AppComponents = AppFeatures;

  constructor(
    private store: Store<AppState>,
    private objectCreatorService: ObjectCreatorService,
    public userFacade: UserFacadeService,
    protected router: Router,
    private presentationService: PresentationService,
  ) {
    this.currentObject$ = this.store.select(selectCurrentObject);
    this.currentUser$ = this.store.select(selectUser);
    this.currentObjectEditMode$ = this.store.select(
      selectCurrentObjectEditMode,
    );
    this.isGlobalAdmin$ = this.store.select(selectUserAccessLevelGlobalAdmin);

    this.store
      .select(selectCoreAppSection)
      .pipe(
        tap((section) => {
          this.appSection = section;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .select(selectUserCompany)
      .pipe(
        tap((company) => {
          this.activeCompany = company?.name;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .select(selectUserAccessLevelName)
      .pipe(
        tap((accessLevelName) => {
          this.accessLevel = accessLevelName;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  public ngOnInit(): void {}

  showAlerts(): void {
    if (this.activeMenu === 'alerts') {
      this.alertsContainer.clear();
      this.activeMenu = 'none';
      return;
    }

    this.activeMenu = 'alerts';

    this.alertsContainer.clear();
    this.notificationsContainer.clear();
    this.userContainer.clear();

    const componentRef = this.alertsContainer.createComponent(
      DropdownAlertsComponent,
    );

    componentRef.instance.closeMethod = () => {
      this.alertsContainer.clear();
      if (this.activeMenu === 'alerts') {
        this.activeMenu = 'none';
      }
    };
  }

  showNotifications(): void {
    if (this.activeMenu === 'notifications') {
      this.notificationsContainer.clear();
      this.activeMenu = 'none';
      return;
    }

    this.activeMenu = 'notifications';

    this.notificationsContainer.clear();
    this.alertsContainer.clear();
    this.userContainer.clear();

    const componentRef = this.notificationsContainer.createComponent(
      DropdownNotificationsComponent,
    );

    componentRef.instance.closeMethod = () => {
      this.notificationsContainer.clear();
      if (this.activeMenu === 'notifications') {
        this.activeMenu = 'none';
      }
    };
  }

  showUserSettings(): void {
    if (this.activeMenu === 'user') {
      this.userContainer.clear();
      this.activeMenu = 'none';
      return;
    }

    this.activeMenu = 'user';

    this.userContainer.clear();
    this.notificationsContainer.clear();
    this.alertsContainer.clear();

    const componentRef = this.userContainer.createComponent(
      DropdownUserComponent,
    );

    componentRef.instance.closeMethod = () => {
      this.userContainer.clear();
      if (this.activeMenu === 'user') {
        this.activeMenu = 'none';
      }
    };
  }

  createNewFeed() {
    this.objectCreatorService.createNewFeed();
  }

  createNewWorkspace() {
    this.objectCreatorService.createNewWorkspace();
  }

  createNewDashboard() {
    this.objectCreatorService.createNewDashboard();
  }

  createNewDimension() {
    this.objectCreatorService.createNewDimension();
  }

  createNewUseCase() {
    this.objectCreatorService.createNewUseCase();
  }

  toggleDarkMode() {
    this.userPreferences.toggleDarkMode();
  }

  showPresentationMode(object: Feed | Dashboard) {
    if (object.object_class_id === NomObjectClass.DASHBOARD) {
      this.presentationService.showDashboardPresentation(object.id);
    } else if (object.object_class_id === NomObjectClass.FEED) {
      const objectClass = 'feed';

      this.store
        .select(selectPeriod)
        .pipe(
          take(1),
          tap((period) => {
            const url = this.router.serializeUrl(
              this.router.createUrlTree(
                [
                  environment.baseUrlEnvironmentSuffix,
                  'presentation',
                  objectClass,
                  object.id,
                ],
                {
                  queryParams: {
                    start_time: period.startTimestamp,
                    end_time: period.endTimestamp,
                  },
                },
              ),
            );

            window.open(url, '_blank');
          }),
        )
        .subscribe();
    }
  }
}
